import {useEffect, useState} from "react";
import Tabs from "../components/DashboardTabs";
import type {
  TabLabel,
  UrlSyncArgs,
  ReportSummary,
  Producer,
  ProducerMode,
  AdminDashboardPageProps
} from "@/ReactInit/Scripts/DashboardPage/types";
import DashboardDropdown, {
  type DashboardDropdownGroup,
} from "@/ReactInit/Scripts/DashboardPage/components/DashboardDropdown";
import {
  fetchAllProducers,
  fetchAllReports,
  fetchAllDraftReports,
  fetchReportsCsv,
  publishReport,
  publishReports,
} from "@/ReactInit/Scripts/DashboardPage/api/dashboardApi";
import {toUiErrorModel} from "@/ReactInit/Scripts/DashboardPage/api/dashboardApiError";
import AdminDashboardReportPage from "@/ReactInit/Scripts/DashboardPage/Admin/AdminDashboardReportPage";
import AdminDashboardProducerPage from "@/ReactInit/Scripts/DashboardPage/Admin/AdminDashboardProducerPage";
import DashboardSearchInput from "@/ReactInit/Scripts/DashboardPage/components/DashboardSearchInput";
import {DashboardButton} from "@/ReactInit/Scripts/DashboardPage/components/DashboardButton";
import {
  formatFilenameTimestamp,
  parseDashboardDate,
} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardFormatting";
import {
  tabParamToLabel,
  tabLabelToParam,
  reportSortParamToLabel,
  reportSortLabelToParam,
  userSortParamToLabel,
  userSortLabelToParam,
  cn, isReportSentToFabric
} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";
import DashboardReportEditView from "@/ReactInit/Scripts/DashboardPage/Admin/DashboardReportEditView";
import DashboardProducerDetailsView from "@/ReactInit/Scripts/DashboardPage/Admin/DashboardProducerDetailsView";
import DashboardProducerFormView from "@/ReactInit/Scripts/DashboardPage/Admin/DashboardProducerFormView";
import DashboardReportCreateView from "@/ReactInit/Scripts/DashboardPage/Admin/DashboardReportCreateView";
import {DashboardConfirmationModal} from "@/ReactInit/Scripts/DashboardPage/components/DashboardConfirmationModal";
import {DashboardAlertBox} from "@/ReactInit/Scripts/DashboardPage/components/DashboardAlertBox";
import {useIsMobile} from "@/ReactInit/Scripts/DashboardPage/hooks/useBreakpoint";
import {csvBlobToXlsxBlob} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardExcel";
import {getReportYears} from "@/ReactInit/Scripts/DashboardPage/utils/reportYears";
import {withMinDuration} from "@/ReactInit/Scripts/DashboardPage/utils/minDuration";
import {DashboardLoader} from "@/ReactInit/Scripts/DashboardPage/components/DashboardLoader";

const PUBLISH_SINGLE_CONFIRM_STORAGE_KEY = "dashboard.confirm.publish.single";
const PUBLISH_MULTI_CONFIRM_STORAGE_KEY = "dashboard.confirm.publish.multi";

const syncUrl = (updates: UrlSyncArgs, options?: { mode?: "push" | "replace" }) => {
  if (typeof window === "undefined") return;

  const url = new URL(window.location.href);
  const params = url.searchParams;

  const apply = (key: keyof UrlSyncArgs, paramKey: string) => {
    if (!Object.prototype.hasOwnProperty.call(updates, key)) {
      return;
    }

    const value = updates[key];

    if (
      value === null ||
      value === undefined ||
      (Array.isArray(value) && value.length === 0)
    ) {
      params.delete(paramKey);
      return;
    }

    if (Array.isArray(value)) {
      params.set(paramKey, value.join(","));
    } else {
      params.set(paramKey, String(value));
    }
  };

  apply("tab", "tab");
  apply("reportFilters", "reportFilters");
  apply("reportSort", "reportSort");
  apply("userSort", "userSort");
  apply("userSearch", "userSearch");
  apply("reportId", "reportId");
  apply("reportLimit", "reportLimit");
  apply("producerId", "producerId");

  url.search = params.toString();
  const newUrl = url.toString();

  if (options?.mode === "push") {
    window.history.pushState({}, "", newUrl);
  } else {
    window.history.replaceState({}, "", newUrl);
  }
};

const getEligiblePublishIds = (
  selectedIds: string[],
  reports: ReportSummary[],
): string[] => {
  const byId = new Map(reports.map((r) => [r.id, r]));
  return selectedIds.filter((id) => {
    const r = byId.get(id);
    const reportDt = r ? parseDashboardDate(r.reportDate) : null;
    if (!r || !reportDt) return false;
    return !isReportSentToFabric(r);
  });
};

const producerYearKey = (producerId: string, year: number) =>
  `${producerId}::${year}`;

const isProducerFullyDeactivated = (p: Producer): boolean => {
  const phones = p.phoneNumbers ?? [];
  if (phones.length === 0) return false;
  return phones.every((ph) => !!ph.deactivated);
};

const filterVisibleProducers = (list: Producer[]): Producer[] =>
  list.filter((p) => !isProducerFullyDeactivated(p));

const AdminDashboardPage = ({isDev = false}: AdminDashboardPageProps) => {
  const [activeTab, setActiveTab] = useState<TabLabel>(() => {
    if (typeof window === "undefined") return "Rapportoversikt";
    const url = new URL(window.location.href);
    return tabParamToLabel(url.searchParams.get("tab"));
  });

  const [reportLimit, setReportLimit] = useState<number>(() => {
    if (typeof window === "undefined") return 10;
    const url = new URL(window.location.href);
    const raw = url.searchParams.get("reportLimit");
    const parsed = raw ? Number.parseInt(raw, 10) : NaN;
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
    return 10;
  });

  const [selectedReportId, setSelectedReportId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const url = new URL(window.location.href);
    return url.searchParams.get("reportId");
  });

  const isMobile = useIsMobile();

  const [reports, setReports] = useState<ReportSummary[]>([]);
  const [producers, setProducers] = useState<Producer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorUi, setErrorUi] = useState<null | {
    variant: "error" | "warning" | "info";
    title?: string;
    message: string;
    action?: { label: string; href?: string; onClick?: () => void }
  }>(null);
  const [producerFormMode, setProducerFormMode] = useState<ProducerMode | null>(null);
  const [producerFormProducerId, setProducerFormProducerId] = useState<string | null>(null);
  const [returnToProducerId, setReturnToProducerId] = useState<string | null>(null);
  const [creatingReportProducerId, setCreatingReportProducerId] = useState<string | null>(null);
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);

  const [reportReturnProducerId, setReportReturnProducerId] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState<string>(() => {
    if (typeof window === "undefined") return "";
    const url = new URL(window.location.href);
    return url.searchParams.get("userSearch") ?? "";
  });

  const [reportFilters, setReportFilters] = useState<string[]>(() => {
    if (typeof window === "undefined") return ["ALL"];
    const url = new URL(window.location.href);
    const raw = url.searchParams.get("reportFilters");
    if (!raw) return ["ALL"];
    const values = raw
      .split(",")
      .map((v) => decodeURIComponent(v))
      .filter(Boolean);
    return values.length ? values : ["ALL"];
  });

  const [reportSort, setReportSort] = useState<string>(() => {
    if (typeof window === "undefined") return "Nyeste";
    const url = new URL(window.location.href);
    return reportSortParamToLabel(url.searchParams.get("reportSort"));
  });

  const [userSort, setUserSort] = useState<string>(() => {
    if (typeof window === "undefined") return "Nyeste";
    const url = new URL(window.location.href);
    return userSortParamToLabel(url.searchParams.get("userSort"));
  });

  const [selectedReportIds, setSelectedReportIds] = useState<string[]>([]);
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);

  const eligiblePublishIds = getEligiblePublishIds(selectedReportIds, reports);
  const eligiblePublishCount = eligiblePublishIds.length;

  const [showSidebar, setShowSidebar] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const url = new URL(window.location.href);
    const reportIdFromUrl = url.searchParams.get("reportId");
    const producerIdFromUrl = url.searchParams.get("producerId");
    return !(reportIdFromUrl || producerIdFromUrl);
  });

  const [selectedProducerId, setSelectedProducerId] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const url = new URL(window.location.href);
    return url.searchParams.get("producerId");
  });

  const getPublishConflictMessage = (
    eligibleIds: string[],
    freshReports: ReportSummary[],
    allReportsForValidation: ReportSummary[],
  ): string | null => {
    if (eligibleIds.length === 0) return null;

    const producerNameById = new Map(producers.map((p) => [p.id, p.name]));
    const allById = new Map(allReportsForValidation.map((r) => [r.id, r]));
    const freshById = new Map(freshReports.map((r) => [r.id, r]));

    const eligibleReports = eligibleIds
      .map((id) => allById.get(id) ?? freshById.get(id))
      .filter((r): r is ReportSummary => !!r);

    const sentByProducerYear = new Map<string, ReportSummary>();
    allReportsForValidation.forEach((r) => {
      if (!isReportSentToFabric(r)) return;
      sentByProducerYear.set(producerYearKey(r.producerId, r.year), r);
    });

    for (const report of eligibleReports) {
      const key = producerYearKey(report.producerId, report.year);
      const alreadySent = sentByProducerYear.get(key);
      if (alreadySent && alreadySent.id !== report.id) {
        const producerName =
          producerNameById.get(report.producerId) ?? "denne virksomheten";
        return `Kan ikke sende til Fabric: ${producerName} har allerede en sendt rapport for ${report.year}.`;
      }
    }

    const seen = new Set<string>();
    for (const report of eligibleReports) {
      const key = producerYearKey(report.producerId, report.year);
      if (seen.has(key)) {
        const producerName =
          producerNameById.get(report.producerId) ?? "denne virksomheten";
        return `Kan ikke sende til Fabric: flere valgte rapporter gjelder ${producerName} for ${report.year}. Velg kun én rapport per virksomhet og år.`;
      }
      seen.add(key);
    }

    return null;
  };

  useEffect(() => {
    let isCancelled = false;

    async function loadData() {
      setIsLoading(true);
      setError(null);
      const errorMessage = "Noe gikk galt ved henting av data.";

      try {
        if (activeTab === "Rapportoversikt") {
          const draftsOnly = reportFilters.includes("DRAFTS_ONLY");

          const [reportsData, producersData] = await Promise.all([
            draftsOnly ? fetchAllDraftReports() : fetchAllReports(),
            fetchAllProducers(),
          ]);

          if (!isCancelled) {
            setReports(reportsData);
            setProducers(filterVisibleProducers(producersData));
          }
        } else if (activeTab === "Brukeradministrasjon") {
          const data = await fetchAllProducers();
          if (!isCancelled) {
            setProducers(filterVisibleProducers(data));
          }
        }
      } catch (e) {
        if (!isCancelled) {
          if (isDev) {
            console.error(e);
          }
          setErrorUi(toUiErrorModel(e));
          setError(
            e instanceof Error
              ? isDev ? e.message : errorMessage : errorMessage
          );
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }

    loadData();

    return () => {
      isCancelled = true;
    };
  }, [activeTab, reportFilters, isDev]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    syncUrl({userSearch: term || null});
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    syncUrl({userSearch: null});
  };

  const handleToggleReport = (id: string) => {
    setSelectedReportIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleToggleAllReports = (allIds: string[]) => {
    const allSelected = allIds.every((id) => selectedReportIds.includes(id));
    if (allSelected) {
      setSelectedReportIds([]);
    } else {
      setSelectedReportIds(allIds);
    }
  };

  const handleChangeReportLimit = (next: number) => {
    const safe = next > 0 ? next : 10;
    setReportLimit(safe);
    syncUrl({
      reportLimit: String(safe),
    });
  };

  useEffect(() => {
    if (activeTab !== "Brukeradministrasjon") return;

    setReportLimit(10);
    syncUrl({reportLimit: "10"}, {mode: "replace"});
  }, [activeTab, searchTerm, userSort]);

  const handleDownloadCsv = async () => {
    if (!selectedReportIds.length) {
      setError("Velg minst én rapport for å laste ned Excel-filen.");
      return;
    }

    try {
      setError(null);
      setIsDownloadingCsv(true);
      const csvBlob = await withMinDuration(() => fetchReportsCsv(selectedReportIds));
      const xlsxBlob = await csvBlobToXlsxBlob(csvBlob, {sheetName: "Rapporter"});
      const url = window.URL.createObjectURL(xlsxBlob);
      const timestamp = formatFilenameTimestamp();
      const filename = `rapport-${timestamp}.xlsx`;
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (isDev) {
        console.error(e);
      }
      setErrorUi(toUiErrorModel(e));
      setError(
        e instanceof Error
          ? e.message
          : "Noe gikk galt ved nedlasting av Excel-filen.",
      );
    } finally {
      setIsDownloadingCsv(false);
    }
  };

  const {currentYear} = getReportYears();
  const [createReportYear, setCreateReportYear] = useState<number>(currentYear);
  const reportYearFilterItems = Array.from(
    new Set(reports.map((r) => r.year).filter((y) => Number.isFinite(y))),
  )
    .sort((a, b) => b - a)
    .map((year) => ({value: `YEAR_${year}`, label: String(year)}));

  const draftsOnlyActive = reportFilters.includes("DRAFTS_ONLY");

  const withDisabled = (
    groups: DashboardDropdownGroup[],
    isItemDisabled: (value: string) => boolean,
    reason?: string,
  ): DashboardDropdownGroup[] =>
    groups.map((g) => ({
      ...g,
      items: g.items.map((it) => ({
        ...it,
        disabled: isItemDisabled(it.value),
        disabledReason: isItemDisabled(it.value) ? reason : undefined,
      })),
    }));

  const reportFilterGroups: DashboardDropdownGroup[] = [
    {
      items: [
        {value: "ALL", label: "Alle"},
        {value: "DRAFTS_ONLY", label: "Alle utkast"},
      ],
    },
    ...(reportYearFilterItems.length > 0 ? [{items: reportYearFilterItems}] : []),
    {
      items: [
        {value: "EDITED", label: "Redigert"},
        {value: "NOT_EDITED", label: "Ikke redigert"},
      ],
    },
    {
      items: [
        {value: "PUBLISHED", label: "Sendt til Fabric"},
        {value: "NOT_PUBLISHED", label: "Ikke sendt til Fabric"},
      ],
    },
  ];

  const reportFilterGroupsUi = withDisabled(
    reportFilterGroups,
    (value) => draftsOnlyActive && value !== "ALL" && value !== "DRAFTS_ONLY",
    "Andre filter er deaktivert når 'Alle utkast' er valgt.",
  );

  const handleProducerDeleted = async (deletedProducerId: string) => {
    try {
      await refreshUsers();
    } catch (e) {
      if (isDev) console.error(e);
      setErrorUi(toUiErrorModel(e));
    }

    if (selectedProducerId === deletedProducerId) {
      setSelectedProducerId(null);
      setShowSidebar(true);
      setCreatingReportProducerId(null);

      syncUrl({producerId: null}, {mode: "replace"});
    }
  };

  const handleReportFilterChange = (values: string[]) => {
    let next: string[];

    const wantsDraftsOnly = values.includes("DRAFTS_ONLY");
    const wantsAll = values.includes("ALL");

    if (wantsDraftsOnly) {
      next = ["DRAFTS_ONLY"];
    } else if (wantsAll) {
      next = ["ALL"];
    } else {
      next = values.filter((v) => v !== "ALL" && v !== "DRAFTS_ONLY");
      if (next.length === 0) next = ["ALL"];
    }

    setReportFilters(next);

    const paramValues = next.includes("ALL") ? [] : next;
    syncUrl({reportFilters: paramValues.length ? paramValues : null});
  };

  const openPublishSelected = () => {
    if (selectedReportIds.length === 0) {
      setPublishError("Velg minst én rapport.");
      return;
    }

    setPublishError(null);

    (async () => {
      try {
        const {eligibleIds, freshReports, conflictMessage} =
          await getFreshEligiblePublishIds();
        setReports(freshReports);

        if (conflictMessage) {
          setPublishError(conflictMessage);
          return;
        }

        if (eligibleIds.length === 0) {
          setPublishError("Alle valgte rapporter er allerede sendt til Fabric.");
          return;
        }

        const isMulti = eligibleIds.length > 1;
        const key = isMulti
          ? PUBLISH_MULTI_CONFIRM_STORAGE_KEY
          : PUBLISH_SINGLE_CONFIRM_STORAGE_KEY;

        try {
          if (localStorage.getItem(key) === "1") {
            void handleConfirmPublishSelected();
            return;
          }
        } catch {
          // ignore
        }

        setIsPublishModalOpen(true);
      } catch (e) {
        if (isDev) console.error(e);
        setErrorUi(toUiErrorModel(e));
        setPublishError("Kunne ikke verifisere rapportstatus før sending til Fabric.");
      }
    })();
  };

  const handleCancelPublishSelected = () => {
    if (isPublishing) return;
    setIsPublishModalOpen(false);
  };

  const handleConfirmPublishSelected = async () => {
    const errorMessage = "Noe gikk galt ved publisering.";

    try {
      setIsPublishing(true);
      setPublishError(null);
      const {eligibleIds, freshReports, conflictMessage} =
        await getFreshEligiblePublishIds();
      setReports(freshReports);

      if (conflictMessage) {
        setIsPublishModalOpen(false);
        setPublishError(conflictMessage);
        return;
      }

      if (eligibleIds.length === 0) {
        setIsPublishModalOpen(false);
        setPublishError("Alle valgte rapporter er allerede sendt til Fabric.");
        setSelectedReportIds([]);
        return;
      }

      if (eligibleIds.length === 1) {
        await withMinDuration(() => publishReport(eligibleIds[0]));
      } else {
        await withMinDuration(() => publishReports(eligibleIds));
      }

      setIsPublishModalOpen(false);

      await refreshReports();
      setSelectedReportIds([]);
    } catch (e) {
      if (isDev) console.error(e);
      const msg = e instanceof Error ? (isDev ? e.message : errorMessage) : errorMessage;
      setErrorUi(toUiErrorModel(e));
      setPublishError(msg);
      throw new Error(msg);
    } finally {
      setIsPublishing(false);
    }
  };

  const openCreateProducer = () => {
    setProducerFormMode("create");
    setCreatingReportProducerId(null);
    setProducerFormProducerId(null);
    setReturnToProducerId(null);
    setSelectedProducerId(null);
    setShowSidebar(false);

    syncUrl(
      {tab: tabLabelToParam("Brukeradministrasjon"), producerId: null, reportId: null},
      {mode: "push"}
    );
  };

  const openEditProducer = (producerId: string) => {
    setProducerFormMode("edit");
    setCreatingReportProducerId(null);
    setProducerFormProducerId(producerId);
    setReturnToProducerId(producerId);
    setSelectedProducerId(null);
    setShowSidebar(false);

    syncUrl(
      {tab: tabLabelToParam("Brukeradministrasjon"), producerId: producerId, reportId: null},
      {mode: "push"}
    );
  };

  const closeProducerForm = () => {
    setProducerFormMode(null);
    setProducerFormProducerId(null);
    setCreatingReportProducerId(null);

    if (returnToProducerId) {
      setSelectedProducerId(returnToProducerId);
      setShowSidebar(false);
      syncUrl({producerId: returnToProducerId}, {mode: "replace"});
    } else {
      setSelectedProducerId(null);
      setShowSidebar(true);
      syncUrl({producerId: null}, {mode: "replace"});
    }

    setReturnToProducerId(null);
  };

  const handleProducerSaved = async () => {
    await refreshUsers();
    closeProducerForm();
  };

  const reportSortGroups: DashboardDropdownGroup[] = [
    {
      items: [
        {value: "Nyeste", label: "Nyeste"},
        {value: "Eldste", label: "Eldste"},
        {value: "Alfabetisk", label: "Alfabetisk"},
      ],
    },
  ];

  const userSortGroups: DashboardDropdownGroup[] = [
    {
      items: [
        {value: "Nyeste", label: "Nyeste"},
        {value: "Eldste", label: "Eldste"},
        {value: "Alfabetisk", label: "Alfabetisk"},
      ],
    },
  ];

  const filteredSortedReports: ReportSummary[] = (() => {
    if (reports.length === 0) return [];

    const selected = reportFilters;
    const hasAll = selected.includes("ALL") || selected.includes("DRAFTS_ONLY") || selected.length === 0;

    let filtered = reports;

    if (!hasAll) {
      const yearFilters = selected
        .filter((v) => v.startsWith("YEAR_"))
        .map((v) => Number.parseInt(v.split("_")[1] ?? "", 10))
        .filter((n) => !Number.isNaN(n));

      const filterEdited = {
        edited: selected.includes("EDITED"),
        notEdited: selected.includes("NOT_EDITED"),
      };

      const filterPublished = {
        published: selected.includes("PUBLISHED"),
        notPublished: selected.includes("NOT_PUBLISHED"),
      };

      filtered = reports.filter((r) => {
        const changedDate = parseDashboardDate(r.changedDate);
        const reportDate = parseDashboardDate(r.reportDate);

        const isEdited =
          !!changedDate &&
          !!reportDate &&
          changedDate.getTime() > reportDate.getTime();

        const isPublished = isReportSentToFabric(r);

        let ok = true;

        if (yearFilters.length > 0) {
          ok = ok && yearFilters.includes(r.year);
        }

        if (filterEdited.edited !== filterEdited.notEdited) {
          ok = ok && (filterEdited.edited ? isEdited : !isEdited);
        }

        if (filterPublished.published !== filterPublished.notPublished) {
          ok = ok && (filterPublished.published ? isPublished : !isPublished);
        }

        return ok;
      });
    }

    const sorted = [...filtered];

    const getProducerName = (producerId: string): string => {
      const p = producers.find((prod) => prod.id === producerId);
      return (p?.name ?? "").toLowerCase();
    };

    switch (reportSort) {
      case "Eldste":
        sorted.sort((a, b) => a.year - b.year);
        break;
      case "Alfabetisk":
        sorted.sort((a, b) =>
          getProducerName(a.producerId).localeCompare(
            getProducerName(b.producerId),
            "nb",
          ),
        );
        break;
      case "Nyeste":
      default:
        sorted.sort((a, b) => b.year - a.year);
        break;
    }

    return sorted;
  })();

  const handleTabChange = (tab: string) => {
    const label = tab as TabLabel;
    setActiveTab(label);
    setReportLimit(10);
    syncUrl({reportLimit: "10"}, {mode: "replace"});
    setSelectedReportId(null);
    setSelectedProducerId(null);
    setShowSidebar(true);
    setCreatingReportProducerId(null);
    setProducerFormMode(null);

    syncUrl(
      {
        tab: tabLabelToParam(label),
        reportId: null,
        producerId: null,
      },
      {mode: "push"}
    );
  };


  const handleSelectReport = (id: string) => {
    setReportReturnProducerId(null);
    setSelectedReportId(id);
    setShowSidebar(false);
    syncUrl(
      {
        tab: tabLabelToParam("Rapportoversikt"),
        reportId: id,
      },
      {mode: "push"}
    );
  };

  const handleCloseReport = async () => {
    const backProducerId = reportReturnProducerId;

    setSelectedReportId(null);
    setReportReturnProducerId(null);

    try {
      await refreshReports();
    } catch (e) {
      if (isDev) console.error(e);
      setErrorUi(toUiErrorModel(e));
    }

    if (backProducerId) {
      setActiveTab("Brukeradministrasjon");
      setSelectedProducerId(backProducerId);
      setCreatingReportProducerId(null);
      setShowSidebar(false);

      syncUrl(
        {
          tab: tabLabelToParam("Brukeradministrasjon"),
          producerId: backProducerId,
          reportId: null,
        },
        {mode: "push"},
      );

      return;
    }

    setShowSidebar(true);
    syncUrl({reportId: null});
  };

  const handleReportDeleted = (deletedId: string) => {
    setReports((prev) => prev.filter((r) => r.id !== deletedId));
    setSelectedReportIds((prev) => prev.filter((id) => id !== deletedId));
    setSelectedReportId(null);
    setShowSidebar(true);

    syncUrl({
      reportId: null,
    });
  };

  const handleSelectProducer = (id: string) => {
    setSelectedProducerId(id);
    setShowSidebar(false);
    setCreatingReportProducerId(null);

    syncUrl(
      {
        tab: tabLabelToParam("Brukeradministrasjon"),
        producerId: id,
        reportId: null,
      },
      {mode: "push"}
    );
  };

  const handleCloseProducer = () => {
    setSelectedProducerId(null);
    setShowSidebar(true);
    setCreatingReportProducerId(null);

    syncUrl({producerId: null});
  };

  useEffect(() => {
    if (typeof window === "undefined") return;

    const url = new URL(window.location.href);
    const params = url.searchParams;

    if (!params.has("tab")) {
      syncUrl({
        tab: tabLabelToParam(activeTab),
      });
    }
  }, [activeTab]);

  const openCreateReport = (producerId: string) => {
    setCreateReportYear(currentYear);
    setCreatingReportProducerId(producerId);
    setSelectedProducerId(producerId);
    setShowSidebar(false);

    syncUrl(
      {
        tab: tabLabelToParam("Brukeradministrasjon"),
        producerId,
        reportId: null,
      },
      {mode: "push"},
    );
  };

  const closeCreateReport = () => {
    setCreatingReportProducerId(null);

    if (selectedProducerId) {
      setShowSidebar(false);
      syncUrl({producerId: selectedProducerId}, {mode: "replace"});
    } else {
      setShowSidebar(true);
      syncUrl({producerId: null}, {mode: "replace"});
    }
  };


  useEffect(() => {
    if (typeof window === "undefined") return;

    const handlePopState = () => {
      const url = new URL(window.location.href);
      const params = url.searchParams;
      const tabParam = params.get("tab");
      const reportIdParam = params.get("reportId");
      const reportFiltersParam = params.get("reportFilters");
      const reportSortParam = params.get("reportSort");
      const userSortParam = params.get("userSort");
      const userSearchParam = params.get("userSearch");
      const reportLimitParam = params.get("reportLimit");
      const producerIdParam = params.get("producerId");
      const nextTab = tabParamToLabel(tabParam);

      setActiveTab(nextTab);
      setSelectedReportId(reportIdParam);
      setShowSidebar(!(reportIdParam || producerIdParam));
      setSearchTerm(userSearchParam ?? "");
      setCreatingReportProducerId(null);
      setProducerFormMode(null);
      setProducerFormProducerId(null);
      setReturnToProducerId(null);

      if (reportFiltersParam) {
        const values = reportFiltersParam
          .split(",")
          .map((v) => decodeURIComponent(v))
          .filter(Boolean);
        setReportFilters(values.length ? values : ["ALL"]);
      } else {
        setReportFilters(["ALL"]);
      }

      setReportSort(reportSortParamToLabel(reportSortParam));
      setUserSort(userSortParamToLabel(userSortParam));
      setSelectedReportIds([]);
      setSelectedProducerId(producerIdParam);

      if (reportLimitParam) {
        const parsed = Number.parseInt(reportLimitParam, 10);
        setReportLimit(!Number.isNaN(parsed) && parsed > 0 ? parsed : 10);
      } else {
        setReportLimit(10);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const getFreshEligiblePublishIds = async (): Promise<{
    eligibleIds: string[];
    freshReports: ReportSummary[];
    conflictMessage: string | null;
  }> => {
    const draftsOnly = reportFilters.includes("DRAFTS_ONLY");
    const freshReports = draftsOnly
      ? await fetchAllDraftReports()
      : await fetchAllReports();
    const allReportsForValidation = await fetchAllReports();

    const eligibleIds = getEligiblePublishIds(selectedReportIds, freshReports);
    const conflictMessage = getPublishConflictMessage(
      eligibleIds,
      freshReports,
      allReportsForValidation
    );

    return {eligibleIds, freshReports, conflictMessage};
  };

  const refreshReports = async () => {
    const draftsOnly = reportFilters.includes("DRAFTS_ONLY");
    const data = draftsOnly ? await fetchAllDraftReports() : await fetchAllReports();
    setReports(data);
    setSelectedReportIds((prev) => {
      const allowed = new Set(data.map((r) => r.id));
      return prev.filter((id) => allowed.has(id));
    });
  };


  const refreshUsers = async () => {
    const data = await fetchAllProducers();
    setProducers(filterVisibleProducers(data));
  };

  if (errorUi) {
    return (
      <div className="flex flex-col gap-3">
        <DashboardAlertBox
          type={errorUi.variant}
          label={`${errorUi.title ? `${errorUi.title}: ` : ""}${errorUi.message}`}
        />
        {errorUi.action?.href && (
          <a className="underline" href={errorUi.action.href}>
            {errorUi.action.label}
          </a>
        )}
        {errorUi.action?.onClick && (
          <DashboardButton
            variant="darkplain"
            label={errorUi.action.label}
            onClick={errorUi.action.onClick}
          />
        )}
      </div>
    )
  }

  const isDraftReport = (r: ReportSummary): boolean => !parseDashboardDate(r.reportDate);

  const draftReportIdForSelectedProducer = (() => {
    if (!selectedProducerId) return null;

    const draft = reports.find(
      (r) => r.producerId === selectedProducerId && isDraftReport(r)
    );

    return draft?.id ?? null;
  })();

  return (
    <>
      <div id="dashboard" className="mb-40">
        <Tabs
          tabs={["Rapportoversikt", "Brukeradministrasjon"]}
          activeTab={activeTab}
          onChange={handleTabChange}
        />

        <div id="dashboard-inner" className="flex flex-col tablet:flex-row gap-5 tablet:gap-10 mt-10">
          {showSidebar && (
            <div className="flex flex-col gap-5 min-w-52 tablet:min-w-60">
              {activeTab === "Rapportoversikt" && (
                <>
                  <DashboardDropdown
                    groups={reportFilterGroupsUi}
                    selectedValues={reportFilters}
                    onChange={handleReportFilterChange}
                    label="Filter"
                    multiple
                  />
                  <DashboardDropdown
                    groups={reportSortGroups}
                    selectedValues={[reportSort]}
                    onChange={(values) => {
                      const next = values[0] ?? "Nyeste";
                      setReportSort(next);
                      syncUrl({
                        reportSort: reportSortLabelToParam(next),
                      });
                    }}
                    label="Sorter"
                  />
                  {!isMobile && <hr className="my-5 border-t border-disabled-bg"/>}
                  <div className={cn(
                    isMobile && selectedReportIds.length === 0 && "hidden",
                    isMobile && "fixed bottom-0 left-0 w-full backdrop-blur-xl bg-brown-25/30 z-20 py-4 px-2 bg-linear-to-b",
                    "flex flex-row tablet:flex-col gap-3 tablet:gap-5 justify-center tablet:justify-start",
                  )}>
                    <DashboardButton
                      onClick={handleDownloadCsv}
                      label={`Last ned Excel-fil${selectedReportIds.length ? ` (${selectedReportIds.length})` : ''}`}
                      loading={isDownloadingCsv}
                      disabled={selectedReportIds.length === 0}
                      variant="sidebar"
                      labelClass="text-base"
                    />

                    <DashboardButton
                      onClick={openPublishSelected}
                      label={`Send til Fabric${eligiblePublishCount ? ` (${eligiblePublishCount})` : ''}`}
                      disabled={eligiblePublishCount === 0}
                      variant="sidebar"
                      labelClass="text-base"
                    />
                  </div>
                </>
              )}

              {activeTab === "Brukeradministrasjon" && (
                <>
                  <DashboardSearchInput
                    onSearch={handleSearch}
                    value={searchTerm}
                    label="Søk"
                    placeholder="Virksomhet / organisasjonsnummer"
                  />
                  <DashboardDropdown
                    groups={userSortGroups}
                    selectedValues={[userSort]}
                    onChange={(values) => {
                      const next = values[0] ?? "Nyeste";
                      setUserSort(next);
                      syncUrl({userSort: userSortLabelToParam(next)});
                    }}
                    label="Sorter"
                  />
                </>
              )}
            </div>
          )}

          <div className="flex flex-col gap-4 min-w-0 w-full">
            {isLoading && <DashboardLoader label="Laster data…"/>}
            {error && <DashboardAlertBox label={`En feil oppstod: ${error}`} type="error"/>}
            {publishError && <DashboardAlertBox label={`En feil oppstod: ${publishError}`} type="error"/>}

            {!isLoading && !error && activeTab === "Rapportoversikt" && (
              <>
                {selectedReportId ? (
                  <DashboardReportEditView
                    reportId={selectedReportId}
                    onBack={() => handleCloseReport()}
                    allReports={reports}
                    onReportDeleted={handleReportDeleted}
                    onReportPublished={async () => {
                      try {
                        await refreshReports();
                      } catch (e) {
                        if (isDev) {
                          console.error(e);
                        }
                        setErrorUi(toUiErrorModel(e));
                      }
                    }}
                    onReportChanged={() => void refreshReports()}
                    isAdmin
                  />
                ) : (
                  <AdminDashboardReportPage
                    reports={filteredSortedReports}
                    producers={producers}
                    selectedReportIds={selectedReportIds}
                    onToggleReport={handleToggleReport}
                    onToggleAllReports={handleToggleAllReports}
                    onSelectReport={handleSelectReport}
                    visibleCount={reportLimit}
                    onChangeVisibleCount={handleChangeReportLimit}
                    reportFilters={reportFilters}
                  />
                )}
              </>
            )}

            {!isLoading && !error && activeTab === "Brukeradministrasjon" && (
              <>
                {producerFormMode ? (
                  <DashboardProducerFormView
                    mode={producerFormMode}
                    producer={
                      producerFormMode === "edit"
                        ? producers.find((p) => p.id === producerFormProducerId) ?? null
                        : null
                    }
                    onCancel={closeProducerForm}
                    onSaved={() => void handleProducerSaved()}
                  />
                ) : selectedProducerId ? (
                  creatingReportProducerId ? (
                    <DashboardReportCreateView
                      producerId={creatingReportProducerId}
                      year={createReportYear}
                      onYearChange={setCreateReportYear}
                      onCancel={closeCreateReport}
                      onCreated={(newReportId) => {
                        if (isDev) {
                          console.log("draft created:", newReportId);
                        }
                      }}
                      onConfirmed={async (newReportId) => {
                        try {
                          await refreshReports();
                        } catch (e) {
                          if (isDev) {
                            console.error(e);
                          }
                          setErrorUi(toUiErrorModel(e));
                        }

                        setActiveTab("Rapportoversikt");
                        setSelectedReportId(newReportId);
                        setSelectedProducerId(null);
                        setShowSidebar(false);
                        setCreatingReportProducerId(null);

                        syncUrl(
                          {
                            tab: tabLabelToParam("Rapportoversikt"),
                            reportId: newReportId,
                            producerId: null,
                          },
                          {mode: "push"},
                        );
                      }}
                    />

                  ) : (
                    <DashboardProducerDetailsView
                      producer={producers.find((p) => p.id === selectedProducerId) ?? null}
                      onBack={handleCloseProducer}
                      onEditProducer={(id) => openEditProducer(id)}
                      onCreateReport={(id) => openCreateReport(id)}
                      onProducerDeleted={(id) => void handleProducerDeleted(id)}
                      draftReportId={draftReportIdForSelectedProducer}
                      onOpenDraftReport={(draftId) => {
                        setReportReturnProducerId(selectedProducerId);
                        setActiveTab("Rapportoversikt");
                        setSelectedReportId(draftId);
                        setSelectedProducerId(null);
                        setShowSidebar(false);
                        setCreatingReportProducerId(null);

                        syncUrl(
                          {
                            tab: tabLabelToParam("Rapportoversikt"),
                            reportId: draftId,
                            producerId: null,
                          },
                          {mode: "push"},
                        );
                      }}
                    />
                  )
                ) : (
                  <AdminDashboardProducerPage
                    users={producers}
                    searchTerm={searchTerm}
                    sortOption={userSort}
                    onResetFilters={handleResetFilters}
                    onRefreshUsers={refreshUsers}
                    onSelectProducer={handleSelectProducer}
                    onCreateProducer={openCreateProducer}
                    visibleCount={reportLimit}
                    onChangeVisibleCount={handleChangeReportLimit}
                  />

                )}

              </>
            )}

            <DashboardConfirmationModal
              isOpen={isPublishModalOpen}
              type="confirmation"
              title={
                eligiblePublishCount <= 1
                  ? "Er du sikker på at du vil sende til Fabric?"
                  : `Er du sikker på at du vil sende ${eligiblePublishCount} rapporter til Fabric?`
              }
              description={
                eligiblePublishCount <= 1
                  ? "Dette betyr at dataene i rapporten blir lagret og inkludert i Power BI-diagrammet. De publiserte dataene vil være tilgjengelige for offentligheten."
                  : "Dette betyr at dataene i rapportene blir lagret og inkludert i Power BI-diagrammet. De publiserte dataene vil være tilgjengelige for offentligheten."
              }
              confirmLabel={isPublishing ? "Sender…" : "Ja, send til Fabric"}
              cancelLabel="Gå tilbake"
              onConfirm={handleConfirmPublishSelected}
              onCancel={handleCancelPublishSelected}
              confirmDisabled={isPublishing}
              cancelDisabled={isPublishing}
              rememberKey={
                eligiblePublishCount <= 1
                  ? PUBLISH_SINGLE_CONFIRM_STORAGE_KEY
                  : PUBLISH_MULTI_CONFIRM_STORAGE_KEY
              }
              rememberLabel="Ikke vis denne meldingen neste gang"
            />

          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboardPage;
